import { useEffect, useState } from "react";
import {
  getMethodologyOverview,
  MethodologyOverview,
} from "@/api/methodologyConsole";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  EmptyState,
  ErrorState,
  LifecycleBadge,
  LoadingState,
  ReasonCodes,
  StatCard,
} from "./primitives";
import FeatureStatePanel from "./FeatureStatePanel";

const countTotal = (m?: Record<string, number> | null) =>
  Object.values(m ?? {}).reduce((a, b) => a + Number(b), 0);

const StatusBreakdown = ({ map }: { map?: Record<string, number> | null }) => {
  const entries = Object.entries(map ?? {});
  if (entries.length === 0)
    return <span className="text-sm text-muted-foreground">None recorded</span>;
  return (
    <div className="flex flex-wrap gap-2">
      {entries.map(([status, count]) => (
        <span key={status} className="flex items-center gap-1">
          <LifecycleBadge status={status} />
          <span className="text-sm tabular-nums">{count}</span>
        </span>
      ))}
    </div>
  );
};

export default function OverviewTab() {
  const [data, setData] = useState<MethodologyOverview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    getMethodologyOverview()
      .then((d) => active && setData(d))
      .catch((e: Error) => active && setError(e.message));
    return () => {
      active = false;
    };
  }, []);

  if (error) return <ErrorState message={error} />;
  if (!data) return <LoadingState label="Loading methodology overview…" />;

  const runtimeReady = Boolean(data.runtime_activation?.ready);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Module versions"
          value={data.modules.version_count}
          hint={`${data.modules.module_count} modules · ${data.modules.blocked_count} publication blocked`}
        />
        <StatCard
          label="Question versions"
          value={countTotal(data.question_versions)}
          hint={`${data.question_versions.published ?? 0} published`}
        />
        <StatCard
          label="Blueprints"
          value={countTotal(data.blueprints)}
          hint={`${data.blueprints.approved ?? 0} approved`}
        />
        <StatCard
          label="Runtime executions"
          value={data.executions.total}
          hint={`${data.executions.succeeded} succeeded · ${data.executions.failed} failed`}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Taxonomy lifecycle</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {(data.taxonomy ?? []).length === 0 ? (
              <EmptyState
                title="No taxonomy versions"
                description="No taxonomy version has been authored in this environment."
              />
            ) : (
              (data.taxonomy ?? []).map((t) => (
                <div
                  key={`${t.taxonomy_key}-${t.version_label}`}
                  className="flex items-center justify-between border-b py-1 last:border-0"
                >
                  <span className="text-sm">
                    {t.taxonomy_key} <span className="text-muted-foreground">{t.version_label}</span>
                  </span>
                  <LifecycleBadge status={t.status} />
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Runtime activation readiness</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">
              {runtimeReady
                ? "The server reports the Runtime as activation-ready."
                : "The server reports the Runtime as NOT activation-ready."}
            </p>
            <ReasonCodes
              codes={(data.runtime_activation?.reason_codes as string[]) ?? []}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Module lifecycle</CardTitle>
          </CardHeader>
          <CardContent>
            <StatusBreakdown map={data.modules.by_status} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Mapping & review status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="mb-1 text-xs uppercase text-muted-foreground">
                Control mapping versions
              </p>
              <StatusBreakdown map={data.control_mappings} />
            </div>
            <div>
              <p className="mb-1 text-xs uppercase text-muted-foreground">
                Publication reviews
              </p>
              <StatusBreakdown map={data.publication_reviews} />
            </div>
          </CardContent>
        </Card>
      </div>

      <FeatureStatePanel flags={data.flags} />
    </div>
  );
}
